import { Temporal } from 'temporal-polyfill'

import { DATE_PRESET_DAYS, type DatePreset } from '../constants/datePresets'
import type { WakeupCall } from '../types/wakeup'
import { getWakeupInstantIso } from './formatting'

const formatDateKeyInTimeZone = (
  instant: Date,
  timeZone: string,
): string | null => {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(instant)

    const year = parts.find((part) => part.type === 'year')?.value
    const month = parts.find((part) => part.type === 'month')?.value
    const day = parts.find((part) => part.type === 'day')?.value

    if (!year || !month || !day) return null

    return `${year}-${month}-${day}`
  } catch {
    return null
  }
}

export const getWakeupHistoryDateKey = (
  wakeupCall: WakeupCall,
  pbxTimeZone: string,
): string | null => {
  const iso = getWakeupInstantIso(wakeupCall)
  if (!iso) return null

  const instant = new Date(iso)
  if (Number.isNaN(instant.getTime())) {
    return null
  }

  return formatDateKeyInTimeZone(instant, pbxTimeZone)
}

export const getWakeupHistoryRelativeDateKey = (
  pbxTimeZone: string,
  daysAgo: number,
  now: Date = new Date(),
): string => {
  const zonedNow = Temporal.Instant.from(now.toISOString()).toZonedDateTimeISO(
    pbxTimeZone,
  )

  return zonedNow.toPlainDate().subtract({ days: daysAgo }).toString()
}

export const filterWakeupHistoryCallsByPreset = (
  calls: WakeupCall[],
  preset: DatePreset,
  pbxTimeZone: string,
  now: Date = new Date(),
): WakeupCall[] => {
  const days = DATE_PRESET_DAYS[preset]
  if (days == null) return calls

  try {
    const today = getWakeupHistoryRelativeDateKey(pbxTimeZone, 0, now)
    const start =
      days === 0
        ? today
        : getWakeupHistoryRelativeDateKey(pbxTimeZone, days, now)

    return calls.filter((wakeupCall) => {
      const callDate = getWakeupHistoryDateKey(wakeupCall, pbxTimeZone)
      return callDate != null && callDate >= start && callDate <= today
    })
  } catch {
    return calls
  }
}
